import React, { ReactElement, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Empty,
  Form,
  Modal,
  Select,
  Tooltip,
  message,
} from 'antd';
import moment from 'moment';
import { validateDateNotInPast } from 'client/utils/formValidations';
import useChangePackage from '../../hooks/useChangePackage';

type Props = {
  packageName?: string;
  packageID: number;
  packageStart?: string;
  packageEnd?: string;
  refetchTeam?: () => void;
};

type ChangePackageForm = {
  packageStart: moment.Moment;
  packageEnd: moment.Moment;
  packageID: number;
  includeExpiredUsers: boolean;
};

export const ChangePackageModal = ({
  packageName,
  packageID,
  packageStart,
  packageEnd,
  refetchTeam,
}: Props): ReactElement => {
  const [modalOpen, setModalOpen] = useState(false);
  const {
    onSubmit,
    isLoading: isLoadingChangePackage,
    setSearchPackageQuery,
    filteredResults,
    hasChangePackagePermission,
  } = useChangePackage(packageID, packageName);
  const [form] = Form.useForm<ChangePackageForm>();

  const newPackageID = Form.useWatch('packageID', form);
  const includeExpiredUsers = Form.useWatch('includeExpiredUsers', form);

  useEffect(() => {
    if (newPackageID !== packageID) {
      form.setFieldValue('packageStart', moment());
    }
  }, [newPackageID]);

  const resetForm = () => {
    const packageIDtoSet = packageID === 0 ? undefined : packageID;

    const startDate = moment(packageStart);
    const endDate = moment(packageEnd);

    form.setFields([
      {
        name: 'packageID',
        value: packageIDtoSet,
      },
      {
        name: 'packageStart',
        value: startDate.isValid() ? startDate : '',
        touched: false,
        errors: [],
      },
      {
        name: 'packageEnd',
        value: endDate.isValid() ? endDate : '',
        touched: false,
        errors: [],
      },
      {
        name: 'includeExpiredUsers',
        value: false,
        touched: false,
        errors: [],
      },
    ]);
  };

  useEffect(() => {
    if (modalOpen) {
      resetForm();
    }
  }, [modalOpen]);

  const handleSubmit = () => {
    form
      .validateFields()
      .then(async (values) => {
        try {
          await onSubmit(
            values.packageID,
            values.packageStart,
            values.packageEnd,
            values.includeExpiredUsers
          );
          message.success("Success! The team's package has been updated.");
          refetchTeam();
        } catch (error) {
          message.error(
            "Error! Failed to update the team's package. Please try again later."
          );
        } finally {
          setModalOpen(false);
        }
      })
      .catch(() => {});
  };

  return (
    <>
      <Button onClick={() => setModalOpen(true)}>Edit Package Settings</Button>
      <Modal
        title="Change Package Details"
        centered
        open={modalOpen}
        onOk={handleSubmit}
        okButtonProps={{
          disabled: isLoadingChangePackage,
          loading: isLoadingChangePackage,
        }}
        cancelButtonProps={{
          disabled: isLoadingChangePackage,
          loading: isLoadingChangePackage,
        }}
        onCancel={() => {
          resetForm();
          setModalOpen(false);
        }}
        okText="Save"
        closable={false}
      >
        <Form
          layout="vertical"
          form={form}
          name="control-hooks"
          onFinish={() => {
            setModalOpen(false);
          }}
          style={{ maxWidth: 600 }}
        >
          <Tooltip
            title={
              hasChangePackagePermission
                ? null
                : 'You do not have permission to change the package settings'
            }
          >
            <Form.Item
              name="packageID"
              label="Package"
              rules={[{ required: true }]}
              help={false}
            >
              <Select
                showSearch
                placeholder="Select a Package"
                options={filteredResults}
                notFoundContent={<Empty />}
                onSearch={setSearchPackageQuery}
                filterOption={false}
                disabled={!hasChangePackagePermission}
              />
            </Form.Item>
          </Tooltip>
          <Form.Item
            name="packageStart"
            label="Start Date"
            rules={[
              { required: true },
              {
                validator: validateDateNotInPast(form, 'packageStart'),
              },
            ]}
            validateTrigger="onChange"
          >
            <DatePicker
              style={{
                width: '100%',
              }}
            />
          </Form.Item>
          <Form.Item
            name="packageEnd"
            label="End Date"
            rules={[
              { required: true, message: '' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('packageStart').isBefore(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error('End date must be greater than start date')
                  );
                },
              }),
            ]}
          >
            <DatePicker
              style={{
                width: '100%',
              }}
            />
          </Form.Item>
          <Form.Item
            name="includeExpiredUsers"
            valuePropName="checked"
            initialValue={false}
          >
            <Checkbox>Also apply to expired users</Checkbox>
          </Form.Item>
          {includeExpiredUsers && (
            <Alert
              message="Expired users will be reactivated and placed onto the new package."
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}
        </Form>
        <Alert
          message="Changing this information will propagate down to all users on the team."
          type="warning"
          showIcon
        />
      </Modal>
    </>
  );
};

export default ChangePackageModal;
